import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
// Coins, not a shop icon: it is the only lucide glyph already proven in
// this project's pinned lucide-react version (Pricing.tsx imports it), and a
// wrong icon name is a failed Docker build, not a cosmetic miss.
import { Check, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/lib/analytics";

// Where Stripe sends a buyer who started checkout from the Telegram studio.
//
// The studio cannot host Stripe inside Telegram's webview, so the purchase
// runs in the phone's browser and lands HERE. This page confirms nothing and
// grants nothing (only the Stripe webhook grants credits).
//
// ONE THING TO DO (2026-09-13). The owner's rule for this moment: say it
// worked in big plain words, and offer exactly ONE button -- back to the
// studio. The button is /go/studio (routes/qr.js), the same address every
// printed code uses, so where it leads (the bot, or the Mini App itself with
// TELEGRAM_STUDIO_LINK) is one server setting. The studio re-reads the balance
// by itself when the customer comes back; nobody is told to refresh anything.
//
// IT DEPENDS WHAT THEY PAID WITH (2026-09-21). That rule was written when a
// card was the only way to pay, and a card really is done by the time Stripe
// sends them here. OXXO is not: the buyer leaves this page holding a printed
// voucher, having paid nothing, and the money lands hours or days later when
// they walk into the shop. "Payment done!" to that customer is the one lie
// that guarantees they never go -- or that they decide they were robbed. So
// the page asks the server what actually happened and says that instead.
// While the answer is in flight nothing is claimed either way.
//
// A cancelled checkout does not stop here: it goes back to the landing page's
// credits section with a plain line (?checkout=cancelled). The route stays for
// sessions whose cancel address is still this page.
//
// A buyer from the Python bot's top-up link comes back with &from=bot
// (services/studio-signin.js checkoutReturns), and the one button is /go/bot.

type SessionStatus = {
  payment_status?: string;
  points?: number | null;
  payment_method_types?: string[];
};

export default function BillingReturn({ outcome }: { outcome: "success" | "cancelled" }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const ok = outcome === "success";
  const sessionId = params.get("session_id");
  const fromBot = params.get("from") === "bot";
  // null = still asking. Nothing is claimed until it resolves.
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    if (ok) trackEvent("studio_checkout_returned", { session_id: sessionId });
  }, [ok, sessionId]);

  useEffect(() => {
    if (!ok || !sessionId) { setAsked(true); return; }
    let live = true;
    fetch(`/api/web/billing/session/${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (live) { setStatus(j); setAsked(true); } })
      // A failed read must not invent an outcome. Fall through to the neutral
      // wording below, which is true whichever way the payment went.
      .catch(() => { if (live) setAsked(true); });
    return () => { live = false; };
  }, [ok, sessionId]);

  if (!ok) return <Navigate to={`/?from=${fromBot ? "bot" : "studio"}&checkout=cancelled#credits`} replace />;

  // Paid outright (card, Link, wallet) -- the original, and still the common case.
  const paid = status?.payment_status === "paid";
  // Session complete but nothing collected yet: a voucher is printed and the
  // shop trip has not happened.
  const voucher = asked && !paid && status?.payment_status === "unpaid";

  const title = paid
    ? t("billingReturn.successTitle")
    : voucher
      ? t("billingReturn.voucherTitle")
      : t("billingReturn.pendingTitle");
  const body = paid
    ? t("billingReturn.successBody")
    : voucher
      ? t("billingReturn.voucherBody")
      : t("billingReturn.pendingBody");
  const hint = paid
    ? t("billingReturn.successHint")
    : voucher
      ? t("billingReturn.voucherHint")
      : t("billingReturn.pendingHint");

  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-background p-4 text-foreground">
      <div
        data-testid="billing-success"
        data-payment-status={asked ? (status?.payment_status || "unknown") : "checking"}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center text-card-foreground shadow-sm"
      >
        <div
          className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${
            voucher ? "bg-amber-100" : "bg-green-100"
          }`}
        >
          {voucher ? (
            <Coins className="h-10 w-10 text-amber-600" />
          ) : (
            <Check className="h-10 w-10 text-green-600" />
          )}
        </div>
        <h1 className="mb-3 text-3xl font-extrabold sm:text-4xl">{title}</h1>
        <p className="mb-8 text-lg text-muted-foreground">{body}</p>
        <a
          href={fromBot ? "/go/bot" : "/go/studio"}
          data-testid="back-to-studio"
          className="block w-full rounded-xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground hover:opacity-90"
        >
          {fromBot ? t("billingReturn.backToBot") : t("billingReturn.backToStudio")}
        </a>
        <p className="mt-4 text-sm text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}
